import { supabase } from '../../lib/supabaseClient';

const BUCKET = 'message-attachments';
const SIGNED_URL_TTL = 3600;

// Types capables d'exécuter du script s'ils sont affichés inline depuis l'origine
// de stockage : servis en téléchargement forcé (Content-Disposition: attachment).
// N'empêche pas l'aperçu <img> des SVG (Content-Disposition est ignoré en subresource).
const FORCE_DOWNLOAD_MIME = ['text/html', 'image/svg+xml'];

export interface MessageAttachment {
  id: string;
  message_id: string;
  uploaded_by: string;
  storage_path: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  signed_url?: string;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export async function uploadMessageAttachment(
  messageId: string,
  file: File,
  uploadedById: string,
): Promise<MessageAttachment> {
  const safeName = sanitizeFilename(file.name);
  const uniqueId = crypto.randomUUID();
  const storagePath = `messages/${messageId}/${uniqueId}_${safeName}`;

  const { error: storageErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type, upsert: false });

  if (storageErr) {
    console.error('[uploadMessageAttachment] storage upload failed:', storageErr);
    throw storageErr;
  }

  const { data, error: dbErr } = await supabase
    .from('message_attachments')
    .insert({
      message_id: messageId,
      uploaded_by: uploadedById,
      storage_path: storagePath,
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
    })
    .select('*')
    .single();

  if (dbErr || !data) {
    console.error('[uploadMessageAttachment] DB insert failed, rolling back storage:', dbErr);
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw dbErr ?? new Error('Insert failed');
  }

  return data as MessageAttachment;
}

export async function getAttachmentsForMessages(
  messageIds: string[],
): Promise<Map<string, MessageAttachment[]>> {
  const result = new Map<string, MessageAttachment[]>();
  if (messageIds.length === 0) return result;

  const { data, error } = await supabase
    .from('message_attachments')
    .select('*')
    .in('message_id', messageIds)
    .order('created_at', { ascending: true });

  if (error) throw error;

  const rows = (data ?? []) as MessageAttachment[];
  if (rows.length === 0) return result;

  const inlinePaths = rows
    .filter((r) => !FORCE_DOWNLOAD_MIME.includes(r.mime_type))
    .map((r) => r.storage_path);
  const downloadPaths = rows
    .filter((r) => FORCE_DOWNLOAD_MIME.includes(r.mime_type))
    .map((r) => r.storage_path);

  const urlMap = new Map<string, string>();
  if (inlinePaths.length > 0) {
    const { data: signedData } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(inlinePaths, SIGNED_URL_TTL);
    for (const s of signedData ?? []) {
      if (s.path && s.signedUrl) urlMap.set(s.path, s.signedUrl);
    }
  }
  if (downloadPaths.length > 0) {
    const { data: signedData } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(downloadPaths, SIGNED_URL_TTL, { download: true });
    for (const s of signedData ?? []) {
      if (s.path && s.signedUrl) urlMap.set(s.path, s.signedUrl);
    }
  }

  for (const row of rows) {
    const withUrl: MessageAttachment = { ...row, signed_url: urlMap.get(row.storage_path) ?? undefined };
    const list = result.get(row.message_id) ?? [];
    list.push(withUrl);
    result.set(row.message_id, list);
  }

  return result;
}

export async function deleteMessageAttachment(
  attachmentId: string,
  storagePath: string,
): Promise<void> {
  const { error: dbErr } = await supabase
    .from('message_attachments')
    .delete()
    .eq('id', attachmentId);
  if (dbErr) throw dbErr;

  const { error: storageErr } = await supabase.storage.from(BUCKET).remove([storagePath]);
  if (storageErr) throw storageErr;
}
